import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import type { SsoProvider } from "../../../../shared/generatedDrizzleSchema";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useSessionTenantEdgeUrlOptions } from "../../../hooks/useSessionTenantOrigin";
import { useTenantCustomDomain } from "../../../hooks/useTenantCustomDomain";
import { getPageUrl } from "../../../lib/stepUrl";
import { useMakeUrl, routes } from "../../../lib/urls";
import { classifyZeroResult } from "../../../lib/zeroResultError";
import { Button } from "../../Button";
import { Banner } from "../../Banner";
import { CountBadge } from "../../CountBadge";
import { Icon } from "../../Icon";
import { Link } from "../../Link";
import { Modal } from "../../Modal";
import {
  ModalContent,
  ModalFooter,
  ModalHead,
  ModalRow,
  ModalRows,
  ModalRowText,
} from "../../ModalCard";
import { Text } from "../../Text";

const Content = styled(ModalContent)`
  width: min(480px, calc(100vw - 32px));
  max-height: min(720px, calc(100dvh - 48px));
  overflow: hidden;
`;

const Body = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  min-height: 0;
  overflow-wrap: anywhere;
`;

const Footer = styled(ModalFooter)`
  flex-shrink: 0;
`;

const DependencyLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--text-primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const WorkflowHeader = styled(DependencyLink)`
  padding: 10px 12px;
  border-bottom: 0.5px solid var(--border-alpha);
  border-radius: 12px 12px 0 0;
  background: var(--alpha4);
  gap: 8px;
`;

function useDependencies(providerId: string | null, includeInherited: boolean) {
  const [branches, details] = useQuery(
    queries.ssoProviderRouteDependencies({ providerId, includeInherited })
  );
  return {
    published: branches.filter((b) => b.name === "main"),
    drafts: branches.filter((b) => b.name !== "main"),
    loading: details.type !== "complete" && details.type !== "error",
    failed: details.type === "error",
  };
}

type Dependencies = ReturnType<typeof useDependencies>;

function DependencyList({ branches }: { branches: Dependencies["published"] }) {
  const makeUrl = useMakeUrl();
  const edgeUrlOptions = useSessionTenantEdgeUrlOptions();
  const customDomain = useTenantCustomDomain();
  return (
    <div role="list" style={{ display: "grid", gap: 8 }}>
      {branches.map((branch) => {
        const workflow = branch.workflow;
        if (!workflow) return null;
        const params = {
          spaceId: workflow.spaceId,
          workflowId: workflow.id,
          branchId: branch.name === "main" ? "" : branch.id,
          step: "",
        };
        return (
          <ModalRows key={branch.id} role="listitem">
            <WorkflowHeader to={makeUrl(routes.space.workflow, params)}>
              <Icon
                name="product-workflows"
                size={16}
                color="var(--text-secondary)"
              />
              <ModalRowText style={{ flex: 1 }}>
                <Text variant="text-regular-md">{workflow.name}</Text>
                {branch.name !== "main" && (
                  <Text variant="text-small" color="var(--text-secondary)">
                    {branch.name}
                  </Text>
                )}
              </ModalRowText>
              <Icon
                name="arrow-up-right"
                size={12}
                color="var(--text-secondary)"
              />
            </WorkflowHeader>
            <div role="list">
              {branch.headCommit?.routes.map((route) => (
                <ModalRow
                  key={route.route}
                  role="listitem"
                  style={{ alignItems: "flex-start" }}
                >
                  <ModalRowText style={{ flex: 1, gap: 4 }}>
                    <DependencyLink
                      to={makeUrl(routes.space.workflow, {
                        ...params,
                        step: route.stepDir,
                      })}
                    >
                      <Text variant="text-regular">{route.stepDir}</Text>
                    </DependencyLink>
                    {workflow.space ? (
                      <DependencyLink
                        to={getPageUrl(
                          workflow.space.slug,
                          route.route,
                          edgeUrlOptions,
                          {
                            branchId: branch.name === "main" ? null : branch.id,
                          },
                          customDomain,
                          workflow.space.customHostname
                        )}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Text
                          variant="code-small"
                          color="var(--text-secondary)"
                        >
                          {route.route}
                        </Text>
                        <Icon
                          name="arrow-up-right"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      </DependencyLink>
                    ) : (
                      <Text variant="code-small" color="var(--text-secondary)">
                        {route.route}
                      </Text>
                    )}
                  </ModalRowText>
                  {!route.routeIdp && (
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      style={{ maxWidth: 88, textAlign: "right" }}
                    >
                      Tenant sign-in
                    </Text>
                  )}
                </ModalRow>
              ))}
            </div>
          </ModalRows>
        );
      })}
    </div>
  );
}

function countRoutes(branches: Dependencies["published"]) {
  return branches.reduce(
    (count, branch) => count + (branch.headCommit?.routes.length ?? 0),
    0
  );
}

function DependencySection({
  title,
  branches,
  note,
}: {
  title: string;
  branches: Dependencies["published"];
  note?: string;
}) {
  const routeCount = countRoutes(branches);
  return (
    <section style={{ display: "grid", gap: 8 }} aria-label={title}>
      <ModalHead>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text variant="text-regular-md">{title}</Text>
          <CountBadge>
            <Text variant="text-small-md">{routeCount}</Text>
          </CountBadge>
        </div>
        {note && (
          <Text variant="text-small" color="var(--text-secondary)">
            {note}
          </Text>
        )}
      </ModalHead>
      {branches.length ? (
        <DependencyList branches={branches} />
      ) : (
        <ModalRows style={{ padding: 12 }}>
          <Text variant="text-regular" color="var(--text-secondary)">
            No affected published routes visible to you.
          </Text>
        </ModalRows>
      )}
    </section>
  );
}

function DependencyPreview({
  dependencies,
  draftNote,
  footnote,
}: {
  dependencies: Dependencies;
  draftNote: string;
  footnote: string;
}) {
  if (dependencies.loading || dependencies.failed) {
    return (
      <Text variant="text-regular" color="var(--text-secondary)">
        {dependencies.failed
          ? "Couldn’t load affected routes."
          : "Loading affected routes…"}
      </Text>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <DependencySection
        title="Published routes"
        branches={dependencies.published}
      />
      {dependencies.drafts.length > 0 && (
        <DependencySection
          title="Drafts"
          branches={dependencies.drafts}
          note={draftNote}
        />
      )}
      <Text variant="text-small" color="var(--text-secondary)">
        {footnote}
      </Text>
    </div>
  );
}

export function PrimaryChangeModal({
  providerId,
  isPrimary,
  confirmLabel,
  replacing,
  onCancel,
  onConfirm,
}: {
  providerId: string;
  isPrimary: boolean;
  confirmLabel: string;
  replacing?: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [providers] = useQuery(queries.ssoProviders());
  const current = providers.find((p) => p.isPrimary)?.providerId;
  const dependencies = useDependencies(null, true);
  const name = providerId ? `“${providerId}”` : "this provider";
  const target = isPrimary ? name : "built-in sign-in";
  const routeCount = countRoutes(dependencies.published);
  return (
    <Modal open onClose={onCancel} alert>
      <Content>
        <Body>
          <ModalHead style={{ gap: 8 }}>
            <Text
              variant="text-regular-md"
              style={{ fontSize: 14, lineHeight: "20px" }}
            >
              {replacing
                ? `Replace “${replacing}”?`
                : isPrimary
                  ? `Use ${name} for tenant sign-in`
                  : `Stop using ${name} for tenant sign-in`}
            </Text>
            <Text variant="text-regular" color="var(--text-secondary)">
              {replacing
                ? `People will sign in with ${name} instead.`
                : `Tenant sign-in will use ${target}${
                    isPrimary && current && current !== providerId
                      ? ` instead of “${current}”`
                      : ""
                  }. Published routes using tenant sign-in will switch too. Routes using a specific provider won’t change.`}
            </Text>
          </ModalHead>
          {replacing ? (
            routeCount > 0 && (
              <section
                style={{ display: "grid", gap: 8 }}
                aria-label="Published routes"
              >
                <Text variant="text-small" color="var(--text-secondary)">
                  {routeCount === 1
                    ? "1 published route will switch:"
                    : `${routeCount} published routes will switch:`}
                </Text>
                <DependencyList branches={dependencies.published} />
              </section>
            )
          ) : (
            <DependencyPreview
              dependencies={dependencies}
              draftNote="Draft routes that inherit tenant sign-in will use it once published."
              footnote="Only workflows you can access are listed."
            />
          )}
        </Body>
        <Footer>
          <Button size="l" variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="l"
            variant="primary"
            disabled={dependencies.loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </Footer>
      </Content>
    </Modal>
  );
}

export function DeleteSsoProviderModal({
  provider,
  onClose,
  onDeleted,
}: {
  provider: SsoProvider;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const z = useZero();
  const dependencies = useDependencies(
    provider.providerId,
    provider.isPrimary ?? false
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const blocked = dependencies.published.length > 0;

  async function confirm() {
    setSaving(true);
    setError("");
    try {
      const result = await z.mutate(
        sharedMutators.deleteSSOProvider({ id: provider.id })
      ).server;
      if (result.type === "error") {
        const decision = classifyZeroResult(result);
        setError(
          (decision.kind === "toast" && decision.message) ||
            "Couldn’t delete the SSO provider. Try again."
        );
      } else {
        onDeleted();
        onClose();
      }
    } catch {
      setError("Couldn’t delete the SSO provider. Try again.");
    }
    setSaving(false);
  }

  return (
    <Modal open onClose={onClose} alert>
      <Content>
        <Body>
          <ModalHead style={{ gap: 8 }}>
            <Text
              variant="text-regular-md"
              style={{ fontSize: 14, lineHeight: "20px" }}
            >
              Delete SSO provider
            </Text>
            <Text variant="text-regular" color="var(--text-secondary)">
              {`Users will no longer be able to sign in with “${provider.providerId}”.${
                provider.isPrimary
                  ? " Tenant sign-in and routes that inherit it will use built-in sign-in until you choose another provider."
                  : ""
              } This can’t be undone.`}
            </Text>
          </ModalHead>
          {blocked && (
            <Banner>
              Before you can delete this provider, switch its published routes
              to another provider or turn them off, then republish their
              workflows.
            </Banner>
          )}
          <DependencyPreview
            dependencies={dependencies}
            draftNote="Draft references don’t block deletion. Review them before publishing."
            footnote="Only workflows you can access are listed. Deletion is checked against all published routes."
          />
          {error && (
            <div role="alert">
              <Text variant="text-regular" color="var(--red-600)">
                {error}
              </Text>
            </div>
          )}
        </Body>
        <Footer>
          <Button size="l" variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="l"
            variant="danger"
            disabled={blocked || saving || dependencies.loading}
            onClick={confirm}
          >
            {saving ? "Deleting…" : "Delete"}
          </Button>
        </Footer>
      </Content>
    </Modal>
  );
}
